import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { signIn, useSession } from "../lib/auth-client";
import { readReturnTo, rememberReturnTo, returnToPath } from "../lib/auth-return";

const SIGN_IN_FAILED = "Sign-in failed. Try again.";
const SIGN_IN_RATE_LIMITED = "Too many sign-in attempts. Try again in a minute.";

/**
 * better-auth's client RETURNS `{ error }` for a non-2xx response instead of
 * throwing (#3194), so the rate limit's 429 (#3097) and any server refusal
 * arrive here, not in the catch.
 */
function signInErrorMessage(error: { status?: number; message?: unknown } | null | undefined): string {
  if (error?.status === 429) return SIGN_IN_RATE_LIMITED;
  const message = typeof error?.message === "string" ? error.message.trim() : "";
  return message || SIGN_IN_FAILED;
}

export function Login() {
  const { data: session, isPending } = useSession();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<null | "google" | "microsoft">(null);
  const location = useLocation();
  const returnTo = readReturnTo(location.search);

  if (!isPending && session?.user) {
    return <Navigate to={returnToPath("/auth/return", returnTo)} replace />;
  }

  async function handleSignIn(provider: "google" | "microsoft") {
    setError("");
    setLoading(provider);
    rememberReturnTo(returnTo);
    try {
      const result = await signIn.social({
        provider,
        // better-auth stores this in OAuth state and restores it after the
        // provider's GET callback. AuthReturn performs the final full reload.
        callbackURL: returnToPath("/auth/return", returnTo),
      });
      // Success leaves the button on "Redirecting..." while the page navigates away.
      if (result?.error) {
        setError(signInErrorMessage(result.error));
        setLoading(null);
      }
    } catch {
      setError(SIGN_IN_FAILED);
      setLoading(null);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 p-8">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Crosbe AI</h1>
          <p className="text-sm text-muted-foreground">
            Production Intelligence Platform
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Primary action: Microsoft sign-in for Crosbe staff (jezweb.net Google
            access is via the small admin link below). Hiding the Google button
            prevents Crosbe team from accidentally clicking the wrong provider —
            per Jez 2026-05-27 14:30 spec. */}
        <button
          type="button"
          onClick={() => handleSignIn("microsoft")}
          disabled={loading !== null}
          className="w-full flex items-center justify-center gap-3 rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {/* Microsoft logo — official 4-square */}
          <svg viewBox="0 0 23 23" width="18" height="18" aria-hidden="true">
            <path fill="#F25022" d="M0 0h11v11H0z" />
            <path fill="#7FBA00" d="M12 0h11v11H12z" />
            <path fill="#00A4EF" d="M0 12h11v11H0z" />
            <path fill="#FFB900" d="M12 12h11v11H12z" />
          </svg>
          {loading === "microsoft" ? "Redirecting..." : "Sign in with Microsoft"}
        </button>

        <p className="text-center text-xs text-muted-foreground leading-relaxed">
          Sign in with your @crosbe.com Microsoft account.
          <br />
          <button
            type="button"
            onClick={() => handleSignIn("google")}
            disabled={loading !== null}
            className="mt-1 underline text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading === "google" ? "Redirecting…" : "Admin? Sign in with Google instead"}
          </button>
        </p>

        {/* #2005: the home-screen steps, readable before signing in. */}
        <p className="border-t border-border pt-5 text-center text-sm">
          <Link to="/install" className="font-medium text-primary underline-offset-4 hover:underline">
            Add Crosbe AI to your phone
          </Link>
        </p>
      </div>
    </div>
  );
}
